'use client';

import { useExperiments } from '../../hooks/use-experiments/use-experiments';
import { FormGroup } from '../../templates/form-group/form-group';
import type {
  SelectButtonContainerProps,
  SelectButtonOptionProps,
  SelectButtonProps,
} from './select-button.types';

function Option({
  label,
  value,
  onChange,
  onClick,
  inputId,
  disabled,
  name,
  inputProps,
}: SelectButtonOptionProps) {
  const { mp_1135_new_components_u65, mp_1287_new_components_o65 } =
    useExperiments();

  if (
    mp_1135_new_components_u65 === 'variant' ||
    mp_1287_new_components_o65 === 'variant'
  ) {
    return (
      <label
        key={`${inputId}-${value}-container`}
        className="relative flex flex-1 cursor-pointer items-center justify-center rounded-md bg-primary-background-50 p-8-8 transition-all hover:bg-secondary-variant-50 has-[:disabled]:pointer-events-none has-[:checked]:bg-secondary-variant-50"
      >
        <input
          className="absolute opacity-0"
          {...inputProps}
          type="radio"
          id={`${inputId}-${value}`}
          onChange={onChange}
          onClick={onClick}
          name={name}
          value={value}
          disabled={disabled}
        />
        <span className="hc-typography--paragraph-lg font-semibold text-primary-900">
          {label}
        </span>
      </label>
    );
  }

  return (
    <div
      key={`${inputId}-${value}-container`}
      className="hc-cluster-layout flex-1"
      style={{
        '--hc-cluster-layout-space': '0',
      }}
    >
      <input
        {...inputProps}
        type="radio"
        id={`${inputId}-${value}`}
        onChange={onChange}
        onClick={onClick}
        name={name}
        value={value}
        disabled={disabled}
      />
      <label className="hc-label" htmlFor={`${inputId}-${value}`}>
        {label}
      </label>
    </div>
  );
}

function Container({
  children,
  disabled,
  errorText,
  label,
  helpText,
  showErrors,
}: SelectButtonContainerProps) {
  const { mp_1135_new_components_u65, mp_1287_new_components_o65 } =
    useExperiments();

  if (
    mp_1135_new_components_u65 === 'variant' ||
    mp_1287_new_components_o65 === 'variant'
  ) {
    return (
      <FormGroup disabled={disabled} error={showErrors}>
        <fieldset className="hc-form-control__select-button">
          <legend className="hc-label"> {label}</legend>
          <div className="flex justify-stretch gap-4-4 rounded-lg border border-primary-900 p-4-4 transition-all hover:border-secondary-900 has-[:active]:scale-[0.98] has-[:checked]:border-secondary-variant-900">
            {children}
          </div>
        </fieldset>
        <span className="hc-help-text"> {helpText}</span>
        <span className="hc-error-text">{errorText}</span>
      </FormGroup>
    );
  }

  return (
    <FormGroup disabled={disabled} error={showErrors}>
      <fieldset className="hc-form-control__select-button">
        <legend className="hc-label"> {label}</legend>
        <div className="hc-form-control__select-button-container">
          {children}
        </div>
      </fieldset>
      <span className="hc-help-text"> {helpText}</span>
      <span className="hc-error-text">{errorText}</span>
    </FormGroup>
  );
}

export function SelectButton(properties: SelectButtonProps) {
  const {
    errorText,
    error,
    name,
    options,
    disabled,
    required,
    onChange,
    onClick,
    id,
  } = properties;
  const showErrors = error || !!errorText;
  const inputId = id || name;
  const containerProperties = { ...properties, showErrors };

  const { ContainerView, OptionView } = properties;
  const SelectContainer = ContainerView ?? Container;
  const SelectOption = OptionView ?? Option;

  return (
    <SelectContainer {...containerProperties}>
      {options.map(({ value, label: optionLabel, ...inputProps }) => (
        <SelectOption
          key={value}
          disabled={disabled}
          required={required}
          inputId={inputId}
          inputProps={inputProps}
          label={optionLabel}
          name={name}
          onChange={onChange ?? inputProps.onChange}
          onClick={onClick ?? inputProps.onClick}
          value={value}
        />
      ))}
    </SelectContainer>
  );
}
